<template>
  <v-data-table :headers="headers"
                :items="debts"
                v-model="selected"
                item-key="orderNr"
                :show-expand="$vuetify.breakpoint.smAndDown"
                :class="{ 'elevation-1': $vuetify.breakpoint.smAndDown }"
                @click:row="expand"
                :expanded.sync="expanded"
                hide-default-footer
                :mobile-breakpoint="0"
                :items-per-page="-1"
                show-select>
    <template #item.debtSummary.state.state="{ item }">
      {{ $t('state.' + item.debtSummary.state.state) }}
    </template>
    <template #item.debtSummary.left="{ item }">
      {{ item.debtSummary.left | currency }}
    </template>
    <template #item.debtSummary.total="{ item }">
      {{ item.debtSummary.total | currency }}
    </template>
    <template v-slot:expanded-item="{ headers, item }" v-if="$vuetify.breakpoint.smAndDown">
      <td :colspan="headers.length">
        <v-row>
          <v-col cols="12">
            <span class="font-weight-bold" data-matomo-mask>{{ $t('debt') }}: </span><span>{{ item.orderNr }}</span>
          </v-col>
          <v-col cols="12">
            <span class="font-weight-bold">{{ $t('mpp-text-5') }}: </span><span>{{ item.debtSummary.left | currency }}</span>
          </v-col>
          <v-col cols="12">
            <span class="font-weight-bold">{{ $t('debtState') }}: </span><span>{{ $t('state.' + item.debtSummary.state.state) }}</span>
          </v-col>
          <v-col cols="12">
            <span class="font-weight-bold">{{ $t('paymentReceiver') }}: </span><span class="ok-subtitle">B2 Impact</span>
          </v-col>
        </v-row>
      </td>
    </template>
    <template slot="footer">
      <v-container>
        <v-row>
          <v-col cols="12" :class="{ 'text-center': $vuetify.breakpoint.mdAndUp }">
            <div class="pr-2 d-inline-block">
              <span class="ok-title">{{ $t('mpp-text-1') }} {{ selected.length }} {{ $t('kpl') }}</span>
            </div>
            <div class="d-inline-block">
              <span class="ok-title">{{ $t('mpp-text-2') }} {{ selectedTotal | currency }}</span>
            </div>
          </v-col>
        </v-row>
        <v-row :justify="$vuetify.breakpoint.mdAndUp ? 'center' : 'start'">
          <v-col cols="12">
            <h3 :class="{ 'my-0 text-center text-h3' : $vuetify.breakpoint.mdAndUp, 'my-0 text-h4': $vuetify.breakpoint.smAndDown }">{{ $t('mpp-text-3') }}</h3>
          </v-col>
          <v-col cols="8" md="2">
            <p class="ok-title mb-3">{{ $t('mpp-text-4') }}</p>
            <v-text-field v-model="amount"
                          type="number"
                          min="5"
                          :max="selectedTotal"
                          step="any"
                          append-icon="fas fa-euro-sign"
                          @blur="$v.amount.$touch()"
                          :error-messages="amountErrors"
                          outlined
                          dense
                          class="text-center"></v-text-field>
          </v-col>
          <v-col cols="12" md="5" v-if="!isCompany">
            <p class="ok-title mb-3">{{ $t('dueDate') }}</p>
            <v-radio-group v-model="desiredDueDate" hide-details row>
              <v-radio class="ok-title" color="color-new-link-blue" :label="$t('firstOfMonth')" value="0"></v-radio>
              <v-radio class="ok-title" color="color-new-link-blue" :label="$t('middleOfMonth')" value="1"></v-radio>
              <v-radio class="ok-title" color="color-new-link-blue" :label="$t('lastOfMonth')" value="2"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" class="text-center">
            <shared-calculate-payment-plan-button :debts="selected" :amount="amount" :desiredDueDate="desiredDueDate" @minAmount="setMinAmount"></shared-calculate-payment-plan-button>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-data-table>
</template>

<script>
  import { required, numeric, between } from 'vuelidate/lib/validators'

  export default {
    props: ['debts'],
    data: function () {
      return {
        amount: null,
        desiredDueDate: '0',
        planType: 0,
        minPaymentAmount: 0,
        selected: this.debts,
        expanded: []
      }
    },
    methods: {
      setMinAmount: function (minAmount) {
        this.minPaymentAmount = minAmount
      },
      expand(value) {
        const index = this.expanded.indexOf(value)
        if (index === -1) {
          this.expanded.push(value)
        } else {
          this.expanded.splice(index, 1)
        }
      }
    },
    computed: {
      selectedTotal () {
        let unpaid = 0
        for (let i = 0; i < this.selected.length; i++) {
          unpaid += this.selected[i].debtSummary.left
        }
        return unpaid
      },
      paymentPlanRequest () {
        var debtNrs = this.selected.map(function (s) { return s.nr })
        return {
          debtNrs: debtNrs,
          type: this.planType,
          amount: this.amount,
          date: parseInt(this.desiredDueDate, 10)
        }
      },
      isCompany () {
        return this.$store.getters.isCompany
      },
      amountErrors () {
        const errors = []
        if (!this.$v.amount.$dirty) return errors
        !this.$v.amount.between && errors.push(this.$t('validation.amount.between') + ' ' + this.minPaymentAmount + ' € - ' + Math.floor(this.selectedTotal) + ' €')
        !this.$v.amount.required && errors.push(this.$t('validation.amount.required'))
        return errors
      },
      headers () {
        let desktopHeaders = [
          { text: this.$t('creditor'), value: 'creditor.lastName' },
          { text: this.$t('mpp-text-5'), value: 'debtSummary.left' },
          { text: this.$t('debtAmount'), value: 'debtSummary.total' },
          { text: this.$t('debtState'), value: 'debtSummary.state.state' },
          { text: this.$t('debt'), value: 'orderNr' }
        ]

        let mobileHeaders = [
          { text: '', value: 'data-table-expand' },
          { text: this.$t('creditor'), value: 'creditor.lastName' },
          { text: this.$t('debtAmount'), value: 'debtSummary.total' }
        ]

        return this.$vuetify.breakpoint.smAndDown ? mobileHeaders : desktopHeaders
      }
    },
    validations: {
      amount: {
        required,
        numeric,
        between (value) {
          return between(this.minPaymentAmount, this.selectedTotal)(value)
        }
      }
    }
  }
</script>

<i18n>
  {
  "fi": {
  "mpp-text-1": "Valittuja velkoja yhteensä",
  "mpp-text-2": "Valittujen velkojen kokonaissumma",
  "mpp-text-3": "Tee valituille veloille maksusuunnitelma:",
  "mpp-text-4": "Syötä kuukausierä",
  "mpp-text-5": "Maksettavaa"
  },
  "se": {
  "mpp-text-1": "Valda skulder totalt",
  "mpp-text-2": "Valda skulders totalsumma",
  "mpp-text-3": "Gör en betalningsplan för valda skulder:",
  "mpp-text-4": "Välj månadsrat",
  "mpp-text-5": "Att betala"
  },
  "en": {
  "mpp-text-1": "Chosen debts total",
  "mpp-text-2": "Total amount",
  "mpp-text-3": "Create a payment plan for chosen debts:",
  "mpp-text-4": "Set monthly amount",
  "mpp-text-5": "Amount to pay"
  }
  }
</i18n>
